import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Save, Upload, Bell, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, authHeaders, resolveImage } from "../lib/api";
import ImageCropper from "./ImageCropper";
import PushPanel from "./PushPanel";

export default function EditProductDialog({ product, categories, open, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: "",
        price: "",
        description: "",
        buy_link: "",
        category_id: "",
    });
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [rawSrc, setRawSrc] = useState(null);
    const [rawName, setRawName] = useState("");
    const [cropOpen, setCropOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pushOpen, setPushOpen] = useState(false);

    useEffect(() => {
        if (product) {
            setForm({
                title: product.title || "",
                price: product.price || "",
                description: product.description || "",
                buy_link: product.buy_link || "",
                category_id: product.category_id || "",
            });
            setFile(null);
            setPreview(null);
            setRawSrc(null);
            setRawName("");
        }
    }, [product]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleFile = (f) => {
        if (!f) return;
        setRawName(f.name || "image.jpg");
        const url = URL.createObjectURL(f);
        setRawSrc(url);
        setCropOpen(true);
    };

    const onCropped = (cropped) => {
        setFile(cropped);
        setPreview(URL.createObjectURL(cropped));
        setCropOpen(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!product) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("title", form.title);
            fd.append("price", form.price);
            fd.append("description", form.description);
            fd.append("buy_link", form.buy_link);
            fd.append("category_id", form.category_id || "");
            if (file) fd.append("image", file);
            const { data } = await api.patch(`/products/${product.id}`, fd, {
                headers: {
                    ...authHeaders(),
                    "Content-Type": "multipart/form-data",
                },
            });
            toast.success(`Updated "${data.title}"`);
            onSaved?.(data);
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const inputCls =
        "w-full bg-black border border-white/20 rounded-lg px-4 py-3 text-white focus:border-[#FF5A00] focus:ring-1 focus:ring-[#FF5A00] outline-none";

    return (
        <AnimatePresence>
            {open && product && (
                <motion.div
                    data-testid="edit-product-dialog"
                    className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.form
                        onSubmit={submit}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full sm:max-w-lg bg-[#121212] rounded-t-3xl sm:rounded-3xl border-t sm:border border-white/10 p-6 flex flex-col relative max-h-[92vh] overflow-y-auto no-scrollbar"
                        initial={{ y: "100%", opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{ type: "spring", damping: 26, stiffness: 260 }}
                    >
                        <button
                            type="button"
                            data-testid="edit-close-btn"
                            onClick={onClose}
                            aria-label="Close"
                            className="absolute top-4 right-4 p-2 bg-black/70 rounded-full text-white/70 hover:text-white hover:bg-[#FF5A00] transition-colors z-10"
                        >
                            <X size={18} />
                        </button>

                        <h2 className="font-display text-2xl uppercase tracking-tight mb-5">
                            Edit piece
                        </h2>

                        <label
                            data-testid="edit-file-dropzone"
                            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 rounded-xl p-4 cursor-pointer hover:border-[#FF5A00] transition-colors overflow-hidden mb-4"
                        >
                            <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden bg-black">
                                <img
                                    src={preview || resolveImage(product.image_url)}
                                    alt={form.title}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex items-center gap-2 text-xs text-white/60 mt-1">
                                <Upload size={14} />
                                {file
                                    ? `${file.name} · 3:4 crop applied`
                                    : "Tap to replace image (you'll crop it)"}
                            </div>
                            <input
                                data-testid="edit-image-input"
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                        </label>

                        <ImageCropper
                            open={cropOpen}
                            src={rawSrc}
                            originalName={rawName}
                            onCancel={() => setCropOpen(false)}
                            onCrop={onCropped}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <input
                                data-testid="edit-title-input"
                                required
                                placeholder="Title"
                                value={form.title}
                                onChange={set("title")}
                                className={`col-span-2 ${inputCls}`}
                            />
                            <input
                                data-testid="edit-price-input"
                                placeholder="Price"
                                value={form.price}
                                onChange={set("price")}
                                className={inputCls}
                            />
                            <input
                                data-testid="edit-buylink-input"
                                placeholder="Buy link"
                                value={form.buy_link}
                                onChange={set("buy_link")}
                                className={inputCls}
                            />
                            <textarea
                                data-testid="edit-description-input"
                                placeholder="Description"
                                value={form.description}
                                onChange={set("description")}
                                rows={4}
                                className={`col-span-2 resize-none ${inputCls}`}
                            />
                            <select
                                data-testid="edit-category-select"
                                value={form.category_id}
                                onChange={set("category_id")}
                                className={`col-span-2 ${inputCls}`}
                            >
                                <option value="">No category</option>
                                {(categories || []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <button
                            data-testid="edit-save-btn"
                            type="submit"
                            disabled={saving}
                            className="w-full mt-5 py-3 kw-orange-bg text-black font-bold rounded-xl uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            <Save size={16} />
                            {saving ? "Saving…" : "Save changes"}
                        </button>

                        <div className="mt-5 pt-5 border-t border-white/10">
                            <button
                                type="button"
                                data-testid="edit-push-toggle"
                                onClick={() => setPushOpen((v) => !v)}
                                className="w-full flex items-center justify-between text-left text-sm uppercase tracking-widest text-white/60 hover:text-white"
                            >
                                <span className="flex items-center gap-2">
                                    <Bell size={14} className="kw-orange" />
                                    Push this drop
                                </span>
                                <ChevronDown
                                    size={16}
                                    className={
                                        "transition-transform " +
                                        (pushOpen ? "rotate-180" : "")
                                    }
                                />
                            </button>
                            {pushOpen && (
                                <div
                                    data-testid="edit-push-panel"
                                    className="mt-4"
                                >
                                    <PushPanel
                                        compact
                                        initial={{
                                            title: form.title || "New drop is live",
                                            body:
                                                form.description?.slice(0, 140) ||
                                                "Tap to see the latest piece",
                                            url: form.buy_link || "/",
                                            image:
                                                preview ||
                                                resolveImage(product.image_url) ||
                                                "",
                                        }}
                                        onSent={() => setPushOpen(false)}
                                    />
                                </div>
                            )}
                        </div>
                    </motion.form>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
